'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import {
  IconMessageCircle, IconX, IconSend, IconPaperclip, IconCheck,
  IconBug, IconBulb, IconQuestionMark, IconListCheck, IconLoader2, IconUserCircle,
  IconCamera, IconCrop, IconDeviceDesktop, IconFolder,
} from '@tabler/icons-react'

// The bubble is gated to internal staff in the layout (role check). Here we only
// hide it on a small denylist of contexts where a ticket bubble makes no sense:
// auth flows, the external vet portal, and public landing pages. Everything else —
// the whole internal app, including any future top-level modules — keeps the bubble
// automatically (no allowlist to maintain on the next IA move).
const EXCLUDED_PATH_PREFIXES = ['/auth', '/vet', '/messe', '/meineTasse', '/ambassador']

// IOPS-115/69: Hinweis, wenn die Screen-Capture-API fehlt (iOS Safari / Android Chrome)
const NO_CAPTURE_HINT = 'Bildschirmaufnahme wird auf diesem Gerät nicht unterstützt – nimm einen Screenshot mit dem Gerät auf und wähle ihn hier aus.'

// IOPS-73: deterministisches Pfad→Projekt-Mapping (gleiches Regelwerk wie die
// Bulk-Migration vom 2026-08-19 und die suggest-project-Heuristik).
function projectFromPath(path: string): string | null {
  const p = path.replace(/^\/(de|en)(?=\/|$)/, '')
  if (/^\/(admin\/)?call-analytics/.test(p)) return 'CALLS'
  if (/^\/(admin\/)?pim(\/|$)/.test(p)) return 'PIM'
  if (/^\/marketing/.test(p)) return 'MKT'
  if (/^\/(wiki|projects|workbench)/.test(p)) return 'TOOLS'
  if (/^\/(admin\/)?accounting(\/|$)/.test(p)) return 'BUHA'
  return null
}

type Category = 'bug' | 'feature_request' | 'question' | 'task' | 'other'
type Assignee =
  | { kind: 'employee'; employee_id: string; label: string; avatar_url?: string | null }
  | { kind: 'ai'; agent: 'futalis_ai' | 'claude_ai'; label: string }

type Attachment = { url: string; path: string; mime: string; size: number; name: string; uploaded_at: string }

type Draft = {
  title: string
  summary: string
  keywords: string[]
  structured_body: Record<string, unknown>
  severity_guess?: string
  module_guess?: string
}

type Classification = {
  intent: string
  confidence: number
  severity_guess?: string
  module_guess?: string
  classifier?: string  // 'n8n' | 'qwen' | 'heuristic'
}

type Project = { code: string; name: string }

type Message =
  | { role: 'user'; content: string; attachments?: Attachment[] }
  | { role: 'assistant'; content: string; category?: Category; draft?: Draft; ticketId?: string; ticketUrl?: string; error?: string;
      classification?: Classification;  // full classifier output, persisted onto the ticket as ai_classification
      suggestedProject?: { code: string; reason: string };  // AI project routing suggestion
      // Question-answer mode (intent='question'): show the FutalisAssist answer instead of a draft.
      answer?: string;
      answerForQuestion?: string;  // the original user question — used if user later says "create ticket anyway"
      answerError?: string;
    }

const CATEGORY_META: Record<Category, { label: string; Icon: typeof IconBug; color: string }> = {
  bug:             { label: 'Bug',             Icon: IconBug,          color: 'text-red-600 bg-red-50 border-red-200' },
  feature_request: { label: 'Feature Request', Icon: IconBulb,         color: 'text-purple-600 bg-purple-50 border-purple-200' },
  question:        { label: 'Frage',           Icon: IconQuestionMark, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  task:            { label: 'Aufgabe',         Icon: IconListCheck,    color: 'text-teal-600 bg-teal-50 border-teal-200' },
  other:           { label: 'Sonstiges',       Icon: IconListCheck,    color: 'text-gray-600 bg-gray-50 border-gray-200' },
}

export default function FeedbackAssistant() {
  const pathname = usePathname() || '/'
  const isExcluded = useMemo(
    () => EXCLUDED_PATH_PREFIXES.some(p => pathname.startsWith(`/de${p}`) || pathname.startsWith(`/en${p}`) || pathname.startsWith(p)),
    [pathname]
  )

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Hi 👋 Beschreibe einen Bug, ein Feature oder eine Frage — ich erstelle daraus einen Ticket-Entwurf, den du editieren und absenden kannst. Screenshots kannst du einfach reinpasten.' },
  ])
  const [input, setInput] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([])
  const [classifying, setClassifying] = useState(false)
  const [submitting, setSubmitting] = useState<number | null>(null)
  const [assignees, setAssignees] = useState<{ ai_agents: Assignee[]; employees: Assignee[] } | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [draftOverrides, setDraftOverrides] = useState<Record<number, { category?: Category; priority?: string; assignee?: Assignee | null; title?: string; summary?: string; project?: string }>>({})
  const [shotMenuOpen, setShotMenuOpen] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  // IOPS-115/69: navigator.mediaDevices.getDisplayMedia existiert nicht auf
  // iOS Safari / Android Chrome. SSR-sicher feature-detecten (initial false,
  // nach Mount gesetzt) — ohne die API wird der Kamera-Button zum Foto-Picker.
  const [canCaptureScreen, setCanCaptureScreen] = useState(false)

  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const mobileShotInputRef = useRef<HTMLInputElement | null>(null)

  const loadAssignees = useCallback(async () => {
    try {
      const r = await fetch('/api/feedback/employees')
      if (r.ok) setAssignees(await r.json())
    } catch (e) {
      console.error('[FeedbackAssistant] load employees', e)
    }
  }, [])

  useEffect(() => {
    if (open && !assignees) loadAssignees()
    if (open && projects.length === 0) {
      fetch('/api/projects?status=active').then(r => r.json())
        .then(j => setProjects((j.projects || []).map((p: { project_code: string; project_name: string }) => ({ code: p.project_code, name: p.project_name }))))
        .catch(() => {})
    }
  }, [open, assignees, loadAssignees, projects.length])

  // SSR-sicher: getDisplayMedia-Verfügbarkeit erst nach Mount prüfen (die
  // Komponente wird einmal serverseitig gerendert — dort gibt es kein navigator).
  useEffect(() => {
    setCanCaptureScreen(!!(navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices))
  }, [])

  // Allow other parts of the app (e.g. the WorkBench "Ticket melden" quick
  // action) to open the bubble via a window event.
  useEffect(() => {
    const openHandler = () => setOpen(true)
    window.addEventListener('futalis:open-feedback', openHandler)
    return () => window.removeEventListener('futalis:open-feedback', openHandler)
  }, [])

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, classifying])

  // Paste handler — only active while panel is open and focused area is the input/panel.
  useEffect(() => {
    if (!open) return
    const onPaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const it of Array.from(items)) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          e.preventDefault()
          const file = it.getAsFile()
          if (file) await uploadFile(file)
        }
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (isExcluded) return null

  async function uploadFile(file: File | Blob, name?: string) {
    const fd = new FormData()
    fd.append('file', file, name || (file as File).name || 'paste.png')
    try {
      const r = await fetch('/api/feedback/upload', { method: 'POST', body: fd })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        console.error('[FeedbackAssistant] upload', j)
        return
      }
      const att: Attachment = await r.json()
      setPendingAttachments(p => [...p, att])
    } catch (e) {
      console.error('[FeedbackAssistant] upload', e)
    }
  }

  // Capture a screenshot of the current tab via the browser's screen-capture
  // API (no DOM-rendering library needed — works with any CSS, pixel-perfect).
  // mode 'full' attaches the whole visible page; 'area' opens a crop overlay.
  async function captureScreenshot(mode: 'full' | 'area') {
    setShotMenuOpen(false)
    // Safety net (IOPS-115/69): ohne getDisplayMedia (iOS Safari / Android
    // Chrome) den nativen Foto-Picker öffnen statt still zu scheitern. Der
    // Catch unten bleibt so nur für vom Nutzer abgebrochene Share-Dialoge.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !('getDisplayMedia' in navigator.mediaDevices)) {
      mobileShotInputRef.current?.click()
      return
    }
    setCapturing(true) // hides the panel so it isn't in the shot
    try {
      // preferCurrentTab pre-selects this tab in Chrome/Edge; other browsers
      // show the normal picker where the user chooses the tab.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        preferCurrentTab: true,
      } as unknown as MediaStreamConstraints)
      const video = document.createElement('video')
      video.srcObject = stream
      await new Promise<void>(res => { video.onloadedmetadata = () => res() })
      await video.play()
      // Give the browser a moment to fade out the share dialog.
      await new Promise(r => setTimeout(r, 400))
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d')!.drawImage(video, 0, 0)
      stream.getTracks().forEach(t => t.stop())

      if (mode === 'full') {
        const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/png'))
        if (blob) await uploadFile(blob, `screenshot-${Date.now()}.png`)
      } else {
        setCropSrc(canvas.toDataURL('image/png'))
      }
    } catch (e) {
      // User cancelled the share dialog — not an error.
      console.debug('[FeedbackAssistant] screenshot cancelled', e)
    } finally {
      setCapturing(false)
    }
  }

  async function handleSend() {
    const text = input.trim()
    if (!text && pendingAttachments.length === 0) return

    const userMsg: Message = { role: 'user', content: text, attachments: pendingAttachments }
    setMessages(m => [...m, userMsg])
    setInput('')
    const attachmentsForDraft = pendingAttachments
    setPendingAttachments([])
    setClassifying(true)

    try {
      const r = await fetch('/api/feedback/classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          page: { url: window.location.href, path: window.location.pathname, title: document.title },
          attachments: attachmentsForDraft.map(a => ({ url: a.url, mime: a.mime, name: a.name })),
        }),
      })
      const j = await r.json()
      const payload = j?.fallback || j
      const intent: Category = (payload?.intent as Category) || 'other'
      const draft: Draft = payload?.draft ?? {
        title: text.slice(0, 80),
        summary: text.slice(0, 280),
        keywords: [],
        structured_body: {},
      }
      const classification: Classification = {
        intent,
        confidence: typeof payload?.confidence === 'number' ? payload.confidence : 0,
        severity_guess: payload?.draft?.severity_guess,
        module_guess: payload?.draft?.module_guess,
        classifier: j?.classifier,
      }
      // Inject screenshots into the draft summary if user only pasted images.
      if (!text && attachmentsForDraft.length > 0 && !draft.title) {
        draft.title = `[${intent.toUpperCase()}] Screenshot von ${window.location.pathname}`
      }

      // For questions: try to actually answer via FutalisAssist instead of
      // showing a ticket draft. Falls back to ticket draft if assistant fails.
      if (intent === 'question') {
        // Show a loading message immediately so the user sees activity.
        const loadingIdx = -1
        setMessages(m => [
          ...m,
          {
            role: 'assistant',
            content: 'Suche nach einer Antwort…',
            category: 'question',
            answerForQuestion: text,
          },
        ])
        try {
          const ar = await fetch('/api/feedback/answer', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              question: text,
              page: { url: window.location.href, path: window.location.pathname, title: document.title },
            }),
          })
          const aj = await ar.json()
          if (!ar.ok) {
            // Fallback: surface the draft so the user can submit as a ticket.
            setMessages(m => m.map((mm, i) => i === m.length - 1 ? {
              ...mm,
              content: `Ich konnte gerade keine Antwort finden. Du kannst die Frage als Ticket einreichen:`,
              category: 'question',
              draft,
              classification,
              answerError: aj?.error || 'unavailable',
            } : mm))
          } else {
            setMessages(m => m.map((mm, i) => i === m.length - 1 ? {
              ...mm,
              content: '',
              answer: aj.answer,
              answerForQuestion: text,
              draft,  // kept available behind a fallback button
              classification,
            } : mm))
          }
        } catch (err) {
          console.error('[FeedbackAssistant] answer', err)
          setMessages(m => m.map((mm, i) => i === m.length - 1 ? {
            ...mm,
            content: `Ich konnte gerade keine Antwort finden. Du kannst die Frage als Ticket einreichen:`,
            category: 'question',
            draft,
            classification,
            answerError: 'network',
          } : mm))
        }
        // Skip the standard "Ich klassifiziere das als …" draft message.
        // (Ignored loadingIdx; using m.length - 1 above.)
        void loadingIdx
        return
      }

      setMessages(m => [
        ...m,
        {
          role: 'assistant',
          content: `Ich klassifiziere das als **${CATEGORY_META[intent]?.label || intent}**. Bitte prüf und ergänze den Entwurf.`,
          category: intent,
          draft,
          classification,
        },
      ])

      // Suggest a target project (async, non-blocking) and attach it to the
      // just-added draft message so the editor can preselect it.
      fetch('/api/feedback/suggest-project', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: `${draft.title}\n${draft.summary}\n${text}`.trim(), page_path: window.location.pathname }),
      }).then(r => r.ok ? r.json() : null).then(j => {
        if (!j?.project_code) return
        if (Array.isArray(j.projects) && j.projects.length) setProjects(j.projects)
        setMessages(m => {
          // Attach to the last assistant message that has a draft but no suggestion yet.
          const idx = [...m].map((mm, i) => ({ mm, i })).reverse().find(x => x.mm.role === 'assistant' && x.mm.draft && !x.mm.suggestedProject)?.i
          if (idx === undefined) return m
          return m.map((mm, i) => i === idx ? { ...mm, suggestedProject: { code: j.project_code, reason: j.reason || '' } } : mm)
        })
      }).catch(() => {})
      // Default: attach the user's just-uploaded attachments to the latest draft.
      // Stored on the message via index after render — for MVP we re-derive when submitting.
    } catch (e) {
      console.error('[FeedbackAssistant] classify', e)
      setMessages(m => [...m, { role: 'assistant', content: 'Konnte gerade nicht klassifizieren — kannst du den Entwurf manuell ausfüllen?', category: 'other', draft: { title: text.slice(0, 80), summary: text, keywords: [], structured_body: {} } }])
    } finally {
      setClassifying(false)
    }
  }

  async function submitDraft(idx: number) {
    const msg = messages[idx]
    if (msg.role !== 'assistant' || !msg.draft || !msg.category) return

    const ov = draftOverrides[idx] || {}
    const category: Category = ov.category || msg.category
    // PMA-505: kein automatischer Vorbelegungswert — leer ist erlaubt
    const priority = ov.priority || null
    // IOPS-65: unberührt (undefined) = Standard futalis AI; explizit null = Nicht zugewiesen
    const assignee = ov.assignee !== undefined
      ? ov.assignee
      : (assignees?.ai_agents.find(a => a.kind === 'ai' && a.agent === 'futalis_ai') ?? null)
    const title = ov.title ?? msg.draft.title
    const summary = ov.summary ?? msg.draft.summary
    const projectCode = ov.project || msg.suggestedProject?.code || 'IOPS'

    // Find the user message just before this assistant message — its attachments
    // are the screenshots the user wanted to attach to *this* ticket.
    let attachments: Attachment[] = []
    for (let i = idx - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role === 'user') { attachments = m.attachments || []; break }
    }

    setSubmitting(idx)
    try {
      const description = renderDescription({
        summary, structured_body: msg.draft.structured_body, attachments,
        page_url: window.location.href, page_path: window.location.pathname, page_title: document.title,
      })

      const r = await fetch('/api/feedback/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_code: projectCode,
          title,
          description,
          category,
          priority,
          source: 'chat_assistant',
          assignee: assignee
            ? assignee.kind === 'ai'
              ? { kind: 'ai', agent: assignee.agent }
              : { kind: 'employee', employee_id: assignee.employee_id }
            : null,
          reported_url: window.location.href,
          reported_path: window.location.pathname,
          user_agent: navigator.userAgent,
          ai_summary: summary,
          ai_keywords: msg.draft.keywords,
          ai_classification: msg.classification
            ? {
                intent: msg.classification.intent,
                confidence: msg.classification.confidence,
                severity_guess: msg.classification.severity_guess,
                module_guess: msg.classification.module_guess,
                classifier: msg.classification.classifier,
              }
            : undefined,
          structured_body: msg.draft.structured_body,
          attachments,
          labels: [category],
        }),
      })
      const j = await r.json()
      if (!r.ok) {
        setMessages(m => m.map((mm, i) => i === idx ? { ...mm, error: j?.error || 'submit_failed' } : mm))
        return
      }
      const code = j?.ticket_id as string
      const locale = pathname.split('/')[1] || 'de'
      const url = `/${locale}/projects/${projectCode}/tasks/${j?.task?.task_number}`
      setMessages(m => [...m, { role: 'assistant', content: `Ticket **${code}** wurde erstellt.`, ticketId: code, ticketUrl: url }])
      // Mark the draft message as submitted by clearing the draft.
      setMessages(m => m.map((mm, i) => i === idx ? { ...mm, draft: undefined, content: `${mm.content}\n\n✅ Eingereicht als ${code}.` } : mm))
    } catch (e) {
      console.error('[FeedbackAssistant] submit', e)
      setMessages(m => m.map((mm, i) => i === idx ? { ...mm, error: 'submit_failed' } : mm))
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          aria-label="Feedback geben"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        >
          <IconMessageCircle size={26} />
        </button>
      )}

      {open && (
        <div className={`fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col ${capturing ? 'invisible' : ''}`}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-teal-600 text-white rounded-t-2xl">
            <div className="flex items-center gap-2">
              <IconMessageCircle size={20} />
              <div>
                <div className="text-sm font-semibold">Feedback &amp; Tickets</div>
                {/* IOPS-73: Headline zeigt das pfadbasierte Zielprojekt statt hardcoded IOPS */}
                <div className="text-[11px] text-teal-100">
                  {projectFromPath(pathname) ? `→ Projekt ${projectFromPath(pathname)} (automatisch)` : '→ Projekt wird automatisch zugeordnet'}
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="schließen" className="p-1 rounded hover:bg-white/10">
              <IconX size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-teal-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                  <div>{m.content}</div>

                  {m.role === 'user' && m.attachments && m.attachments.length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-1">
                      {m.attachments.map((a, i) => (
                        a.mime.startsWith('image/')
                          ? <img key={i} src={a.url} alt="" className="rounded-md w-full h-16 object-cover" />
                          : <div key={i} className="rounded-md bg-white/20 p-2 text-xs truncate">{a.name}</div>
                      ))}
                    </div>
                  )}

                  {m.role === 'assistant' && m.answer && (
                    <AnswerCard
                      answer={m.answer}
                      onConvertToTicket={() => {
                        // Switch this message into draft-editor mode using the
                        // pre-staged draft. User can still file the question
                        // as a ticket if the answer wasn't useful.
                        setMessages(curr => curr.map((cm, i) => i === idx ? {
                          ...cm,
                          answer: undefined,
                          content: 'Trotzdem als Ticket einreichen — bitte prüfen:',
                          category: 'question',
                        } : cm))
                      }}
                    />
                  )}

                  {m.role === 'assistant' && m.answerError && (
                    <div className="mt-2 text-xs text-red-600">
                      Antwort-System nicht erreichbar ({m.answerError}). Reicht als Ticket weiter.
                    </div>
                  )}

                  {m.role === 'assistant' && m.draft && m.category && !m.answer && (
                    <DraftEditor
                      draft={m.draft}
                      category={m.category}
                      override={draftOverrides[idx] || {}}
                      onOverrideChange={(o) => setDraftOverrides(p => ({ ...p, [idx]: { ...p[idx], ...o } }))}
                      assignees={assignees}
                      projects={projects}
                      suggestedProject={m.suggestedProject}
                      onSubmit={() => submitDraft(idx)}
                      submitting={submitting === idx}
                      error={m.error}
                    />
                  )}

                  {m.role === 'assistant' && m.ticketUrl && (
                    <a href={m.ticketUrl} className="mt-2 inline-flex items-center gap-1 text-teal-700 underline text-sm">
                      <IconCheck size={14} /> Ticket öffnen
                    </a>
                  )}
                </div>
              </div>
            ))}

            {classifying && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                  <IconLoader2 size={14} className="animate-spin" /> Analysiere…
                </div>
              </div>
            )}
          </div>

          {/* Pending attachments row */}
          {pendingAttachments.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-200 bg-white">
              <div className="flex flex-wrap gap-1">
                {pendingAttachments.map((a, i) => (
                  <div key={i} className="relative w-12 h-12 rounded-md overflow-hidden border border-gray-200 group">
                    {a.mime.startsWith('image/')
                      ? <img src={a.url} alt="" className="w-full h-full object-cover" />
                      : <div className="text-[10px] p-1 break-all">{a.name}</div>}
                    <button
                      onClick={() => setPendingAttachments(p => p.filter((_, j) => j !== i))}
                      className="absolute top-0 right-0 bg-black/60 text-white rounded-bl px-1.5 py-0.5 min-w-6 min-h-6 opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
                      aria-label="entfernen"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Composer */}
          <div className="px-3 py-2 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex items-end gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-gray-500 hover:text-teal-600"
                aria-label="Datei anhängen"
              ><IconPaperclip size={18} /></button>
              <div className="relative">
                <button
                  onClick={() => {
                    // IOPS-115/69: ohne Screen-Capture-API (iOS Safari /
                    // Android Chrome) öffnet der Button den nativen Foto-Picker.
                    if (canCaptureScreen) setShotMenuOpen(o => !o)
                    else mobileShotInputRef.current?.click()
                  }}
                  className={`p-2 hover:text-teal-600 ${shotMenuOpen ? 'text-teal-600' : 'text-gray-500'}`}
                  aria-label={canCaptureScreen ? 'Screenshot aufnehmen' : 'Screenshot aus Fotos auswählen'}
                  title={canCaptureScreen ? undefined : NO_CAPTURE_HINT}
                ><IconCamera size={18} /></button>
                {shotMenuOpen && canCaptureScreen && (
                  <div className="absolute bottom-10 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 z-10">
                    <button
                      onClick={() => captureScreenshot('full')}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                    ><IconDeviceDesktop size={15} /> Ganze Seite</button>
                    <button
                      onClick={() => captureScreenshot('area')}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                    ><IconCrop size={15} /> Bereich auswählen</button>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,application/pdf"
                multiple
                onChange={async (e) => {
                  const el = e.target
                  const files = Array.from(el.files || [])
                  for (const f of files) await uploadFile(f)
                  el.value = ''
                }}
              />
              {/* IOPS-115/69: Mobile-Fallback ohne getDisplayMedia — bewusst
                  OHNE capture-Attribut, damit das OS Fotomediathek + Kamera anbietet. */}
              <input
                type="file"
                ref={mobileShotInputRef}
                className="hidden"
                accept="image/*"
                multiple
                onChange={async (e) => {
                  const el = e.target
                  const files = Array.from(el.files || [])
                  for (const f of files) await uploadFile(f)
                  el.value = ''
                }}
              />
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                rows={2}
                placeholder="Beschreibung… (Screenshots: Ctrl/Cmd+V)"
                className="flex-1 resize-none border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={handleSend}
                disabled={classifying || (!input.trim() && pendingAttachments.length === 0)}
                className="p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300"
                aria-label="senden"
              ><IconSend size={18} /></button>
            </div>
            {!canCaptureScreen && (
              <div className="mt-1 text-[10px] leading-snug text-gray-400">
                {NO_CAPTURE_HINT}
              </div>
            )}
          </div>
        </div>
      )}

      {cropSrc && (
        <CropOverlay
          src={cropSrc}
          onConfirm={async (blob) => {
            setCropSrc(null)
            await uploadFile(blob, `screenshot-${Date.now()}.png`)
          }}
          onCancel={() => setCropSrc(null)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Crop overlay — shown after an 'area' screenshot capture. The user drags a
// rectangle over the captured image; the selection is cropped at natural
// resolution and handed back as a PNG blob.
// ---------------------------------------------------------------------------

function CropOverlay({ src, onConfirm, onCancel }: {
  src: string
  onConfirm: (blob: Blob) => void
  onCancel: () => void
}) {
  const imgRef = useRef<HTMLImageElement | null>(null)
  const dragStart = useRef<{ x: number; y: number } | null>(null)
  const [rect, setRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null)

  const getPos = (e: React.PointerEvent) => {
    const b = imgRef.current!.getBoundingClientRect()
    return {
      x: Math.min(Math.max(e.clientX - b.left, 0), b.width),
      y: Math.min(Math.max(e.clientY - b.top, 0), b.height),
    }
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (!imgRef.current) return
    dragStart.current = getPos(e)
    setRect({ ...dragStart.current, w: 0, h: 0 })
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragStart.current || !imgRef.current) return
    const p = getPos(e)
    setRect({
      x: Math.min(dragStart.current.x, p.x),
      y: Math.min(dragStart.current.y, p.y),
      w: Math.abs(p.x - dragStart.current.x),
      h: Math.abs(p.y - dragStart.current.y),
    })
  }
  const onPointerUp = () => { dragStart.current = null }

  const confirm = () => {
    const img = imgRef.current
    if (!rect || rect.w < 5 || rect.h < 5 || !img) return
    const scaleX = img.naturalWidth / img.clientWidth
    const scaleY = img.naturalHeight / img.clientHeight
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(rect.w * scaleX)
    canvas.height = Math.round(rect.h * scaleY)
    canvas.getContext('2d')!.drawImage(
      img,
      rect.x * scaleX, rect.y * scaleY, rect.w * scaleX, rect.h * scaleY,
      0, 0, canvas.width, canvas.height,
    )
    canvas.toBlob(b => { if (b) onConfirm(b) }, 'image/png')
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex flex-col items-center justify-center p-6">
      <div className="text-white text-sm mb-3">Bereich mit der Maus aufziehen, dann übernehmen.</div>
      <div
        className="relative overflow-hidden cursor-crosshair select-none touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img ref={imgRef} src={src} alt="" className="max-w-[90vw] max-h-[72vh] block" draggable={false} />
        {rect && (
          <div
            className="absolute border-2 border-teal-400 bg-teal-400/20 pointer-events-none"
            style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
          />
        )}
      </div>
      <div className="flex gap-2 mt-4">
        <button
          onClick={confirm}
          disabled={!rect || rect.w < 5 || rect.h < 5}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
        >Übernehmen</button>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20"
        >Abbrechen</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Draft editor (inline form inside the assistant bubble)
// ---------------------------------------------------------------------------

function DraftEditor({
  draft, category, override, onOverrideChange, assignees, projects, suggestedProject, onSubmit, submitting, error,
}: {
  draft: Draft
  category: Category
  override: { category?: Category; priority?: string; assignee?: Assignee | null; title?: string; summary?: string; project?: string }
  onOverrideChange: (o: { category?: Category; priority?: string; assignee?: Assignee | null; title?: string; summary?: string; project?: string }) => void
  assignees: { ai_agents: Assignee[]; employees: Assignee[] } | null
  projects: Project[]
  suggestedProject?: { code: string; reason: string }
  onSubmit: () => void
  submitting: boolean
  error?: string
}) {
  const cat = override.category || category
  const meta = CATEGORY_META[cat]
  const Icon = meta.Icon
  const selectedProject = override.project || suggestedProject?.code || 'IOPS'

  return (
    <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-2 text-xs">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${meta.color}`}>
          <Icon size={12} />
          <select
            value={cat}
            onChange={e => onOverrideChange({ category: e.target.value as Category })}
            className="bg-transparent text-xs border-0 focus:outline-none"
          >
            {(Object.keys(CATEGORY_META) as Category[]).map(c => (
              <option key={c} value={c}>{CATEGORY_META[c].label}</option>
            ))}
          </select>
        </span>
        <select
          value={override.priority || ''}
          onChange={e => onOverrideChange({ priority: e.target.value })}
          className="border border-gray-200 rounded px-1 py-0.5 bg-white"
        >
          <option value="">— Keine —</option>
          <option value="urgent">Urgent</option>
          <option value="high">Hoch</option>
          <option value="medium">Mittel</option>
          <option value="low">Niedrig</option>
        </select>
      </div>

      <input
        value={override.title ?? draft.title}
        onChange={e => onOverrideChange({ title: e.target.value })}
        placeholder="Titel"
        className="w-full border border-gray-200 rounded px-2 py-1 bg-white text-sm"
      />
      <textarea
        value={override.summary ?? draft.summary}
        onChange={e => onOverrideChange({ summary: e.target.value })}
        rows={3}
        placeholder="Kurzbeschreibung"
        className="w-full border border-gray-200 rounded px-2 py-1 bg-white"
      />

      {/* Structured body preview (read-only for MVP) */}
      {Object.keys(draft.structured_body || {}).length > 0 && (
        <details className="bg-white border border-gray-200 rounded px-2 py-1">
          <summary className="cursor-pointer text-gray-600">Strukturierte Felder</summary>
          <pre className="mt-1 whitespace-pre-wrap text-[11px] text-gray-700">{JSON.stringify(draft.structured_body, null, 2)}</pre>
        </details>
      )}

      {/* Project routing */}
      <div className="flex items-center gap-2">
        <IconFolder size={14} className="text-gray-500" />
        <select
          value={selectedProject}
          onChange={e => onOverrideChange({ project: e.target.value })}
          className="flex-1 border border-gray-200 rounded px-1 py-0.5 bg-white"
        >
          {(projects.length ? projects : [{ code: 'IOPS', name: 'Internal Operations System' }]).map(p => (
            <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
          ))}
        </select>
        {suggestedProject && !override.project && (
          <span className="text-[10px] text-teal-600 shrink-0" title={suggestedProject.reason}>AI ✦</span>
        )}
      </div>

      {/* Assignee */}
      <div className="flex items-center gap-2">
        <IconUserCircle size={14} className="text-gray-500" />
        {/* IOPS-74: Suchfeld statt langem Dropdown; IOPS-65: Default = futalis AI */}
        <AssigneeCombo
          assignees={assignees}
          value={override.assignee}
          onChange={a => onOverrideChange({ assignee: a })}
        />
      </div>

      {error && <div className="text-red-600">Fehler: {error}</div>}

      <button
        onClick={onSubmit}
        disabled={submitting}
        className="w-full bg-teal-600 text-white rounded px-2 py-1.5 text-sm hover:bg-teal-700 disabled:bg-gray-300 flex items-center justify-center gap-1"
      >
        {submitting ? <><IconLoader2 size={14} className="animate-spin" /> Erstelle Ticket…</> : 'Ticket erstellen'}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Answer card — rendered when intent='question' and FutalisAssist returned
// an answer. Markdown is rendered minimally (links + bold + line breaks) so
// the user can click through to the recommended /admin/* page.
// ---------------------------------------------------------------------------

function AnswerCard({ answer, onConvertToTicket }: { answer: string; onConvertToTicket: () => void }) {
  return (
    <div className="mt-2 space-y-2">
      <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-gray-800">
        <SimpleMarkdown text={answer} />
      </div>
      <button
        onClick={onConvertToTicket}
        className="text-xs text-gray-500 hover:text-teal-700 underline"
      >
        Hat nicht geholfen — als Ticket abschicken
      </button>
    </div>
  )
}

// Tiny markdown renderer just for chat answers: bold (**), links ([t](u)),
// inline code (`...`), and line breaks. Keeps deps zero.
function SimpleMarkdown({ text }: { text: string }) {
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g
  const boldRe = /\*\*([^*]+)\*\*/g
  const codeRe = /`([^`]+)`/g

  // Token-based parse so we don't dangerouslySetInnerHTML.
  const lines = text.split('\n')
  return (
    <div>
      {lines.map((line, li) => {
        const parts: Array<string | React.ReactElement> = []
        let cursor = 0
        // Greedy: walk through the line replacing matches with React nodes.
        const matches: Array<{ start: number; end: number; render: () => React.ReactElement }> = []
        for (const m of line.matchAll(linkRe)) {
          matches.push({ start: m.index!, end: m.index! + m[0].length, render: () => (
            <a key={`l${m.index}`} href={m[2]} className="text-blue-700 underline font-medium">{m[1]}</a>
          )})
        }
        for (const m of line.matchAll(boldRe)) {
          matches.push({ start: m.index!, end: m.index! + m[0].length, render: () => (
            <strong key={`b${m.index}`}>{m[1]}</strong>
          )})
        }
        for (const m of line.matchAll(codeRe)) {
          matches.push({ start: m.index!, end: m.index! + m[0].length, render: () => (
            <code key={`c${m.index}`} className="bg-gray-100 rounded px-1 text-[12px]">{m[1]}</code>
          )})
        }
        matches.sort((a, b) => a.start - b.start)
        // Filter out overlaps (links win over bold etc.)
        const dedup: typeof matches = []
        for (const m of matches) {
          if (dedup.length === 0 || m.start >= dedup[dedup.length - 1].end) dedup.push(m)
        }
        for (const m of dedup) {
          if (m.start > cursor) parts.push(line.slice(cursor, m.start))
          parts.push(m.render())
          cursor = m.end
        }
        if (cursor < line.length) parts.push(line.slice(cursor))
        return <p key={li} className="my-0.5 break-words">{parts.length ? parts : ' '}</p>
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Markdown body builder — single source of truth for what humans + AI see
// ---------------------------------------------------------------------------

function renderDescription(args: {
  summary: string
  structured_body: Record<string, unknown>
  attachments: Attachment[]
  page_url: string
  page_path: string
  page_title: string
}): string {
  const lines: string[] = []
  lines.push('## Summary', args.summary || '_(keine Beschreibung)_', '')
  lines.push('## Reported on')
  lines.push(`- URL: ${args.page_url}`)
  lines.push(`- Page: ${args.page_title}`)
  lines.push(`- At: ${new Date().toISOString()}`)
  lines.push('')

  const sb = args.structured_body || {}
  if (Array.isArray(sb.steps_to_reproduce) && sb.steps_to_reproduce.length) {
    lines.push('## Steps to reproduce')
    sb.steps_to_reproduce.forEach((s, i) => lines.push(`${i + 1}. ${String(s)}`))
    lines.push('')
  }
  if (sb.expected) { lines.push('## Expected behavior', String(sb.expected), '') }
  if (sb.actual)   { lines.push('## Actual behavior',   String(sb.actual),   '') }
  if (sb.user_value)         { lines.push('## User value',         String(sb.user_value),         '') }
  if (sb.proposed_behavior)  { lines.push('## Proposed behavior',  String(sb.proposed_behavior),  '') }
  if (Array.isArray(sb.acceptance_criteria) && sb.acceptance_criteria.length) {
    lines.push('## Acceptance criteria')
    sb.acceptance_criteria.forEach(c => lines.push(`- ${String(c)}`))
    lines.push('')
  }

  if (args.attachments.length > 0) {
    lines.push('## Attachments')
    args.attachments.forEach(a => {
      lines.push(a.mime.startsWith('image/')
        ? `- ![${a.name}](${a.url})`
        : `- [${a.name}](${a.url})`)
    })
  }
  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// AssigneeCombo (IOPS-74/65) — Suchfeld mit Vorschlagsliste statt langem
// Dropdown. value === undefined bedeutet „unberührt“ und spiegelt den
// Submit-Default futalis AI; value === null ist explizit „Nicht zugewiesen“.
// ---------------------------------------------------------------------------
function AssigneeCombo({ assignees, value, onChange }: {
  assignees: { ai_agents: Assignee[]; employees: Assignee[] } | null
  value: Assignee | null | undefined
  onChange: (a: Assignee | null) => void
}) {
  const [comboOpen, setComboOpen] = useState(false)
  const [query, setQuery] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)

  const effective = value !== undefined
    ? value
    : (assignees?.ai_agents.find(a => a.kind === 'ai' && a.agent === 'futalis_ai') ?? null)
  const currentLabel = effective ? effective.label : 'Nicht zugewiesen'

  const q = query.trim().toLowerCase()
  const agents = (assignees?.ai_agents || []).filter(a => a.kind === 'ai' && (!q || a.label.toLowerCase().includes(q)))
  const employees = (assignees?.employees || []).filter(a => a.kind === 'employee' && (!q || a.label.toLowerCase().includes(q)))

  useEffect(() => {
    if (!comboOpen) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setComboOpen(false); setQuery('') }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [comboOpen])

  const pick = (a: Assignee | null) => { onChange(a); setComboOpen(false); setQuery('') }

  return (
    <div ref={boxRef} className="relative flex-1 min-w-0">
      {comboOpen ? (
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') { setComboOpen(false); setQuery('') }
            if (e.key === 'Enter') {
              e.preventDefault()
              const first = agents[0] || employees[0]
              if (first) pick(first)
            }
          }}
          placeholder="Name tippen…"
          className="w-full border border-teal-300 rounded px-1 py-0.5 bg-white focus:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setComboOpen(true)}
          className="w-full text-left border border-gray-200 rounded px-1 py-0.5 bg-white truncate"
        >
          {currentLabel}
        </button>
      )}
      {comboOpen && (
        <div className="absolute left-0 right-0 bottom-full mb-1 max-h-44 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg z-20 text-xs">
          <button type="button" onClick={() => pick(null)} className="w-full text-left px-2 py-1 hover:bg-gray-50 text-gray-500">— Nicht zugewiesen —</button>
          {agents.length > 0 && <div className="px-2 pt-1 text-[10px] uppercase tracking-wide text-gray-400">AI</div>}
          {agents.map(a => a.kind === 'ai' ? (
            <button key={a.agent} type="button" onClick={() => pick(a)} className="w-full text-left px-2 py-1 hover:bg-teal-50">{a.label}</button>
          ) : null)}
          {employees.length > 0 && <div className="px-2 pt-1 text-[10px] uppercase tracking-wide text-gray-400">Mitarbeiter</div>}
          {employees.map(a => a.kind === 'employee' ? (
            <button key={a.employee_id} type="button" onClick={() => pick(a)} className="w-full text-left px-2 py-1 hover:bg-teal-50">{a.label}</button>
          ) : null)}
          {agents.length === 0 && employees.length === 0 && <div className="px-2 py-1 text-gray-400">Keine Treffer</div>}
        </div>
      )}
    </div>
  )
}
